import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { errorToast } from '../../../shared/toast.service';
import EntityMultiSelectModal from './entity-multi-select.modal';
import { IUser } from '../../../user/user.interface';
import { RootState, AppDispatch } from '../../../../store';
import apiClient from '../../../shared/auth.interceptor';
import { firmRefresh } from '../../../shared/auth.functions';
import { syncFirms } from '../../../shared/redux/syncFirms';

interface AssignFirmsToUserModalProps {
  open: boolean;
  handleClose: () => void;
  onSuccess: () => void;
  user?: IUser;
}

const AssignFirmsToUserModal: React.FC<AssignFirmsToUserModalProps> = ({ open, handleClose, onSuccess, user }) => {
  const dispatch = useDispatch<AppDispatch>();
  const firms = useSelector((state: RootState) => state.firmList.firmList);
  const actor = useSelector((state: RootState) => state.currentUser.currentUser);
  // Firms the actor manages (as Super Admin or Admin), except ones this user owns.
  const editableFirms = useMemo(
    () => firms.filter(f => f._id && (f.ownerId === actor?._id || f.users?.includes(actor?._id ?? '')) && f.ownerId !== user?._id),
    [firms, actor?._id, user?._id]
  );
  const options = useMemo(() => editableFirms.map(f => ({ key: f._id!, label: f.name, raw: f })), [editableFirms]);
  const [selectedFirmIds, setSelectedFirmIds] = useState<string[]>([]);
  const [saving, setSaving] = useState(false);
  const [refreshRequired, setRefreshRequired] = useState(false);

  useEffect(() => {
    setSelectedFirmIds(open ? editableFirms.filter(f => f.users?.includes(user?._id ?? '')).map(f => f._id!) : []);
  }, [open, editableFirms, user?._id]);

  const refresh = async () => {
    dispatch(syncFirms(await firmRefresh()));
    setRefreshRequired(false);
    onSuccess();
  };

  const handleSubmit = async () => {
    if (saving || !user?._id) return;
    setSaving(true);
    let changed = false;
    try {
      if (refreshRequired) {
        await refresh();
        return;
      }
      for (const firm of editableFirms) {
        const assigned = firm.users?.includes(user._id) ?? false;
        const selected = selectedFirmIds.includes(firm._id!);
        if (assigned === selected) continue;
        changed = true;
        if (selected) {
          await apiClient.post(`/v1/firm/assign-firm/${firm._id}`, { userId: user._id });
        } else {
          await apiClient.patch(`/v1/firm/unassign-clients/${firm._id}`, { userId: user._id });
        }
      }
      await refresh();
      handleClose();
    } catch (err: any) {
      // Some individual operations may have succeeded; reload before another save.
      if (changed) {
        setRefreshRequired(true);
        try { await refresh(); } catch { /* Keep refresh-only mode until the cache is current. */ }
      }
      errorToast(err?.response?.data?.message ?? 'Unable to update firm access. Review the current selection before retrying.');
    } finally {
      setSaving(false);
    }
  };

  return <EntityMultiSelectModal
    open={open} title="Firms" label="Firms"
    handleClose={() => { if (!saving) handleClose(); }}
    options={options} valueKeys={selectedFirmIds} onChange={setSelectedFirmIds}
    onSubmit={handleSubmit} disabled={saving || !user?._id}
    description="Only firms you manage are shown. Removing a firm removes this user's access to the firm and all its clients."
    submitLabel={refreshRequired ? 'Reload current access' : 'Update'} emptyText="No firms available"
  />;
};

export default AssignFirmsToUserModal;
